import React, { useMemo } from 'react';
import { 
  TrendingUp, 
  DollarSign, 
  Boxes, 
  ClipboardList, 
  AlertTriangle, 
  Clock, 
  CheckCircle2, 
  ArrowRight, 
  Plus, 
  Users, 
  FileText, 
  Calendar, 
  Building2, 
  Stethoscope, 
  Syringe, 
  ShieldCheck, 
  ExternalLink,
  ChevronRight,
  Package,
  Layers,
  ArrowUpRight,
  Scissors,
  PenTool,
  FileArchive,
  Scale,
  Receipt as ReceiptIcon,
  Zap
} from 'lucide-react';
import { 
  UserProfile, 
  FinancialConfig, 
  Quote, 
  AppView 
} from '../../../../types';
import { 
  WorkOrder, 
  Receipt, 
  InventoryBatch, 
  StockMovement, 
  ClinicalPatient, 
  ClinicalVaccination, 
  ClinicalConsultation,
  AccountantAccess 
} from '../../types';
import { formatCurrencyBRL } from '../../../../utils/formatUtils';
import { 
  EnterpriseVerticalId, 
  ENTERPRISE_VERTICALS, 
  detectVerticalFromNiche 
} from '../config/enterpriseVerticals';
import { resolveCompanyName } from '../../../../hooks/useCompanyProfile';

export interface EnterpriseCommandCenterProps {
  user: UserProfile;
  config: FinancialConfig | null;
  vertical?: EnterpriseVerticalId;
  quotes: Quote[];
  workOrders: WorkOrder[];
  receipts: Receipt[];
  inventoryBatches: InventoryBatch[];
  stockMovements: StockMovement[];
  clinicalPatients: ClinicalPatient[];
  clinicalVaccinations: ClinicalVaccination[];
  clinicalConsultations: ClinicalConsultation[];
  accountantAccesses: AccountantAccess[];
  onNavigate: (view: AppView) => void;
  onNewQuote: () => void;
  onNewWorkOrder: () => void;
  onNewBatch: () => void;
  onNewPatient: () => void;
  onOpenTeamModal: () => void;
}

export default function EnterpriseCommandCenter({
  user,
  config,
  vertical,
  quotes = [],
  workOrders = [],
  receipts = [],
  inventoryBatches = [],
  stockMovements = [],
  clinicalPatients = [],
  clinicalVaccinations = [],
  clinicalConsultations = [],
  accountantAccesses = [],
  onNavigate,
  onNewQuote,
  onNewWorkOrder,
  onNewBatch,
  onNewPatient,
  onOpenTeamModal
}: EnterpriseCommandCenterProps) {
  const companyName = resolveCompanyName(user, 'Minha Empresa');
  const activeVertical = vertical || detectVerticalFromNiche(user.niche, user.profession);
  const preset = ENTERPRISE_VERTICALS[activeVertical] || ENTERPRISE_VERTICALS.general_corporate;

  // 1. Cálculos de KPIs em Tempo Real
  const kpis = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // Faturamento do mês (Recibos emitidos no mês + Orçamentos aprovados/pagos)
    const monthReceipts = receipts.filter(r => {
      if (!r.issuedAt) return false;
      const d = new Date(r.issuedAt);
      return !isNaN(d.getTime()) && d.getFullYear() === currentYear && d.getMonth() === currentMonth;
    });
    const totalReceiptsRevenue = monthReceipts.reduce((acc, r) => acc + (r.amountPaid || r.totalServiceValue || 0), 0);

    const monthQuotesApproved = quotes.filter(q => {
      if (!q.createdAt) return false;
      const d = new Date(q.createdAt);
      return !isNaN(d.getTime()) && d.getFullYear() === currentYear && d.getMonth() === currentMonth && (q.status === 'approved' || q.status === 'paid');
    });
    const totalQuotesRevenue = monthQuotesApproved.reduce((acc, q) => acc + (q.finalPrice || q.finalSuggestedPrice || 0), 0);

    // Maior indicador de faturamento entre recibos e orçamentos aprovados
    const grossRevenue = Math.max(totalReceiptsRevenue, totalQuotesRevenue);
    const proLaboreGoal = config?.proLaboreGoal || 5000;
    const goalPercent = proLaboreGoal > 0 ? Math.min(100, Math.round((grossRevenue / proLaboreGoal) * 100)) : 100;

    // Estoque imobilizado e potencial de venda
    let totalStockCost = 0;
    let totalStockSale = 0;
    let expiringBatchesCount = 0;
    const fifteenDaysFromNow = new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000);

    for (const b of inventoryBatches) {
      const qty = b.currentQuantity || 0;
      const cost = qty * (b.costPrice || 0);
      totalStockCost += cost;
      const sale = qty * (b.salePrice && b.salePrice > 0 ? b.salePrice : (b.costPrice || 0) * 1.6);
      totalStockSale += sale;

      if (b.expirationDate) {
        const exp = new Date(b.expirationDate);
        if (!isNaN(exp.getTime()) && exp <= fifteenDaysFromNow && qty > 0) {
          expiringBatchesCount++;
        }
      }
    }

    const grossMarginPercent = totalStockSale > 0 
      ? (((totalStockSale - totalStockCost) / totalStockSale) * 100).toFixed(1)
      : '0.0';

    // Ordens de serviço ativas
    const openOrders = workOrders.filter(w => w.status === 'open' || w.status === 'in_progress');
    const completedOrdersMonth = workOrders.filter(w => {
      if (!w.createdAt) return false;
      const d = new Date(w.createdAt);
      return !isNaN(d.getTime()) && d.getFullYear() === currentYear && d.getMonth() === currentMonth && w.status === 'completed';
    });

    // Vacinas atrasadas (exclusivo para Pet)
    let overdueVaccinesCount = 0;
    if (activeVertical === 'pet_vet') {
      for (const v of clinicalVaccinations) {
        if (v.nextDueDate && v.status !== 'completed') {
          const d = new Date(v.nextDueDate);
          if (!isNaN(d.getTime()) && d < now) {
            overdueVaccinesCount++;
          }
        }
      }
    }

    return {
      grossRevenue,
      goalPercent,
      proLaboreGoal,
      totalStockCost,
      totalStockSale,
      grossMarginPercent,
      expiringBatchesCount,
      openOrdersCount: openOrders.length,
      completedOrdersMonthCount: completedOrdersMonth.length,
      overdueVaccinesCount,
      totalClients: quotes.length + workOrders.length
    };
  }, [receipts, quotes, inventoryBatches, workOrders, clinicalVaccinations, config, activeVertical]);

  // Feed de atividades consolidadas recentes
  const recentActivities = useMemo(() => {
    const list: {
      id: string;
      type: 'quote' | 'work_order' | 'movement';
      typeLabel: string;
      title: string;
      client: string;
      value?: number;
      status: string;
      statusColor: string;
      date: string;
    }[] = [];

    // Orçamentos recentes
    quotes.slice(0, 5).forEach(q => {
      list.push({
        id: q.id,
        type: 'quote',
        typeLabel: activeVertical === 'pet_vet' ? 'Orçamento Pet' : activeVertical === 'medical' ? 'Tratamento' : 'Orçamento',
        title: q.rawServiceName || 'Serviço sob medida',
        client: q.clientName || 'Cliente não identificado',
        value: q.finalPrice || q.finalSuggestedPrice || 0,
        status: q.status === 'paid' ? 'Pago' : q.status === 'approved' ? 'Aprovado' : 'Em Aberto',
        statusColor: q.status === 'paid' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : q.status === 'approved' ? 'bg-blue-50 text-blue-800 border-blue-200' : 'bg-zinc-100 text-zinc-700 border-zinc-200',
        date: q.createdAt || new Date().toISOString()
      });
    });

    // Ordens de serviço recentes
    workOrders.slice(0, 5).forEach(w => {
      list.push({
        id: w.id,
        type: 'work_order',
        typeLabel: activeVertical === 'technical_service' ? 'OS / Seguro' : 'Ordem de Serviço',
        title: `OS #${w.orderNumber}: ${w.title}`,
        client: w.clientName,
        value: w.finalPrice || 0,
        status: w.status === 'completed' ? 'Entregue' : w.status === 'in_progress' ? 'Em Andamento' : 'Aberta',
        statusColor: w.status === 'completed' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : w.status === 'in_progress' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-blue-50 text-blue-800 border-blue-200',
        date: w.createdAt || new Date().toISOString()
      });
    });

    return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 6);
  }, [quotes, workOrders, activeVertical]);

  const formattedToday = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="space-y-6 pb-12 font-sans">
      
      {/* ─── BANNER DE BOAS-VINDAS CORPORATIVO (TEMA CLARO) ─── */}
      <div className="bg-white rounded-2xl border border-zinc-200/90 p-6 sm:p-7 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800">
                {preset.badgeLabel}
              </span>
              <span className="text-[10px] font-semibold text-zinc-400 capitalize">
                {formattedToday}
              </span>
            </div>

            <h1 className="text-xl sm:text-2xl font-black text-zinc-900 tracking-tight">
              Painel da Empresa • {companyName}
            </h1>

            <p className="text-xs text-zinc-500 font-medium">
              {preset.description}
            </p>
          </div>

          {/* Ações Rápidas em Destaque Contextual por Vertical */}
          <div className="flex items-center gap-2 flex-wrap">
            {activeVertical === 'pet_vet' && (
              <button
                type="button"
                onClick={() => onNavigate('calendar')}
                className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-2 transition-all cursor-pointer"
              >
                <Calendar className="w-3.5 h-3.5" />
                <span>Agendar Horário (Consulta ou Banho)</span>
              </button>
            )}

            {activeVertical === 'medical' && (
              <button
                type="button"
                onClick={() => onNavigate('calendar')}
                className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-2 transition-all cursor-pointer"
              >
                <Calendar className="w-3.5 h-3.5" />
                <span>Agendar Consulta</span>
              </button>
            )}

            {activeVertical === 'technical_service' && (
              <button
                type="button"
                onClick={onNewWorkOrder}
                className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-2 transition-all cursor-pointer"
              >
                <ClipboardList className="w-3.5 h-3.5" />
                <span>Nova Ordem de Serviço</span>
              </button>
            )}

            {activeVertical === 'general_corporate' && (
              <button
                type="button"
                onClick={onNewQuote}
                className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-2 transition-all cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Novo Orçamento</span>
              </button>
            )}
            {activeVertical === 'food_icecream' && (
              <button
                type="button"
                onClick={() => onNavigate('speed_pos')}
                className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-2 transition-all cursor-pointer"
              >
                <Scale className="w-3.5 h-3.5" />
                <span>Nova Pesagem (Speed-Checkout)</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => onNavigate('financial_hub')}
              className="px-3.5 py-2 bg-white hover:bg-zinc-50 text-zinc-700 border border-zinc-200 font-bold text-xs rounded-xl shadow-2xs flex items-center gap-2 transition-all cursor-pointer"
            >
              <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
              <span>Contas & Finanças</span>
            </button>

            <button
              type="button"
              onClick={() => onNavigate('calendar')}
              className="px-3.5 py-2 bg-white hover:bg-zinc-50 text-zinc-700 border border-zinc-200 font-bold text-xs rounded-xl shadow-2xs flex items-center gap-2 transition-all cursor-pointer"
            >
              <Calendar className="w-3.5 h-3.5 text-blue-600" />
              <span>Ver Agenda</span>
            </button>
          </div>
        </div>
      </div>

      {/* ─── QUARTETO DE KPIS EXECUTIVOS EM TEMA CLARO ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        
        {/* KPI 1: Faturamento do Mês */}
        <div className="bg-white rounded-2xl border border-zinc-200/90 p-5 shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400">
              Faturamento do Mês
            </span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center justify-center">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-2xl font-black text-zinc-900 font-mono tracking-tight">
              {formatCurrencyBRL(kpis.grossRevenue)}
            </div>
            <div className="flex items-center justify-between text-[11px] text-zinc-500 mt-1">
              <span>Meta do Mês: {formatCurrencyBRL(kpis.proLaboreGoal)}</span>
              <span className="font-bold text-emerald-700">{kpis.goalPercent}%</span>
            </div>
            <div className="w-full h-1.5 bg-zinc-100 rounded-full overflow-hidden mt-2">
              <div 
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, kpis.goalPercent)}%` }}
              />
            </div>
          </div>
        </div>

        {/* KPI 2: Estoque */}
        <div className="bg-white rounded-2xl border border-zinc-200/90 p-5 shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400">
              {activeVertical === 'pet_vet' ? 'Medicamentos & Produtos' : activeVertical === 'technical_service' ? 'Peças em Estoque' : 'Valor Total em Estoque'}
            </span>
            <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-700 border border-indigo-100 flex items-center justify-center">
              <Boxes className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-2xl font-black text-zinc-900 font-mono tracking-tight">
              {formatCurrencyBRL(kpis.totalStockCost)}
            </div>
            <div className="flex items-center justify-between text-[11px] text-zinc-500 mt-1">
              <span>Previsão de Venda: {formatCurrencyBRL(kpis.totalStockSale)}</span>
              <span className="font-bold text-indigo-700">+{kpis.grossMarginPercent}%</span>
            </div>
            <p className="text-[10px] text-zinc-400 mt-2">
              {inventoryBatches.length} lotes de produtos cadastrados
            </p>
          </div>
        </div>

        {/* KPI 3: Ordens de Serviço / Atendimentos */}
        <div className="bg-white rounded-2xl border border-zinc-200/90 p-5 shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400">
              {activeVertical === 'technical_service' ? 'Serviços & Consertos' : activeVertical === 'pet_vet' ? 'Atendimentos do Mês' : 'Ordens de Serviço'}
            </span>
            <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-700 border border-amber-100 flex items-center justify-center">
              <ClipboardList className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-2xl font-black text-zinc-900 font-mono tracking-tight">
              {kpis.openOrdersCount} <span className="text-xs font-medium text-zinc-400">em andamento</span>
            </div>
            <div className="flex items-center justify-between text-[11px] text-zinc-500 mt-1">
              <span>Concluídos no mês:</span>
              <span className="font-bold text-emerald-700">{kpis.completedOrdersMonthCount} finalizados</span>
            </div>
            <p className="text-[10px] text-zinc-400 mt-2">
              {workOrders.length} serviços registrados no total
            </p>
          </div>
        </div>

        {/* KPI 4: Equipe */}
        <div className="bg-white rounded-2xl border border-zinc-200/90 p-5 shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400">
              Sua Equipe
            </span>
            <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-700 border border-blue-100 flex items-center justify-center">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-2xl font-black text-zinc-900 font-mono tracking-tight">
              Até 10 <span className="text-xs font-medium text-zinc-400">profissionais</span>
            </div>
            <div className="flex items-center justify-between text-[11px] text-zinc-500 mt-1">
              <span>Valor da Hora:</span>
              <span className="font-bold text-blue-700">{formatCurrencyBRL(config?.baseHourValue || 50)}/h</span>
            </div>
            <button
              type="button"
              onClick={onOpenTeamModal}
              className="text-[10px] font-black text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-2 cursor-pointer"
            >
              <span>Gerenciar Funcionários e Acessos</span>
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* ─── AVISOS IMPORTANTES (CONTEXTUAL POR VERTICAL) ─── */}
      {(kpis.expiringBatchesCount > 0 || (activeVertical === 'pet_vet' && kpis.overdueVaccinesCount > 0)) ? (
        <div className="bg-amber-50/70 border border-amber-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-2 text-amber-900 font-black text-xs uppercase tracking-wide">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
            <span>Avisos Importantes • Ações Recomendadas</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {kpis.expiringBatchesCount > 0 && (
              <div className="bg-white p-3.5 rounded-xl border border-amber-200/80 shadow-2xs flex items-center justify-between">
                <div className="space-y-0.5">
                  <span className="text-xs font-black text-zinc-900 block">
                    {kpis.expiringBatchesCount} Produtos com Vencimento Próximo
                  </span>
                  <p className="text-[11px] text-zinc-500">
                    {activeVertical === 'pet_vet' 
                      ? 'Medicamentos ou vacinas vencendo em até 15 dias.' 
                      : activeVertical === 'medical'
                      ? 'Medicamentos hospitalares próximos da validade.'
                      : 'Itens em estoque com vencimento próximo.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onNavigate('estoque_hub')}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs rounded-lg shrink-0 ml-3 cursor-pointer"
                >
                  Ver Estoque
                </button>
              </div>
            )}

            {activeVertical === 'pet_vet' && kpis.overdueVaccinesCount > 0 && (
              <div className="bg-white p-3.5 rounded-xl border border-amber-200/80 shadow-2xs flex items-center justify-between">
                <div className="space-y-0.5">
                  <span className="text-xs font-black text-zinc-900 block">
                    {kpis.overdueVaccinesCount} Clientes com Vacinas para Reforçar
                  </span>
                  <p className="text-[11px] text-zinc-500">
                    Tutores com pets que precisam de reforço vacinal.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onNavigate('retorno_vacinas')}
                  className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-lg shrink-0 ml-3 cursor-pointer"
                >
                  Avisar no WhatsApp
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-emerald-50/60 border border-emerald-200/70 rounded-2xl p-4 flex items-center justify-between text-xs text-emerald-900 font-bold">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span>Tudo em dia! Nenhum produto vencendo e agenda organizada.</span>
          </div>
          <button
            type="button"
            onClick={() => onNavigate('portal_contador')}
            className="text-[11px] text-emerald-800 underline hover:text-emerald-950 font-black cursor-pointer"
          >
            Ver Relatório para o Contador
          </button>
        </div>
      )}

      {/* ─── GRID DUPLO: ATIVIDADES RECENTES + ACESSO DEPARTAMENTAL ESPECIALIZADO ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Coluna Esquerda (8 colunas): Tabela Consolidada de Atividades Recentes */}
        <div className="lg:col-span-8 bg-white rounded-2xl border border-zinc-200/90 p-5 sm:p-6 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
            <div>
              <h2 className="text-sm font-black text-zinc-900">
                Últimas Movimentações • {preset.name}
              </h2>
              <p className="text-[11px] text-zinc-400">
                Atendimentos, ordens de serviço e movimentações registradas
              </p>
            </div>
            <button
              type="button"
              onClick={() => onNavigate('history')}
              className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer"
            >
              <span>Ver Histórico</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {recentActivities.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-zinc-100 text-[10px] font-black uppercase text-zinc-400 tracking-wider">
                    <th className="py-2.5 px-2">Tipo</th>
                    <th className="py-2.5 px-2">Identificação / Título</th>
                    <th className="py-2.5 px-2">{preset.terminology.clientLabel}</th>
                    <th className="py-2.5 px-2 text-right">Valor</th>
                    <th className="py-2.5 px-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50 text-xs">
                  {recentActivities.map((act) => (
                    <tr key={act.id} className="hover:bg-zinc-50/80 transition-colors">
                      <td className="py-3 px-2 font-bold text-zinc-500">
                        {act.typeLabel}
                      </td>
                      <td className="py-3 px-2 font-black text-zinc-900 truncate max-w-56">
                        {act.title}
                      </td>
                      <td className="py-3 px-2 text-zinc-600 truncate max-w-40">
                        {act.client}
                      </td>
                      <td className="py-3 px-2 text-right font-mono font-bold text-zinc-900">
                        {act.value ? formatCurrencyBRL(act.value) : '—'}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${act.statusColor}`}>
                          {act.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center text-zinc-400 space-y-2">
              <Package className="w-8 h-8 mx-auto stroke-1" />
              <p className="text-xs">Nenhuma movimentação registrada recentemente.</p>
              <button
                type="button"
                onClick={onNewQuote}
                className="text-xs font-bold text-blue-600 underline cursor-pointer"
              >
                Registrar Primeiro Atendimento
              </button>
            </div>
          )}
        </div>

        {/* Coluna Direita (4 colunas): Acesso Rápido Especializado por Vertical */}
        <div className="lg:col-span-4 space-y-4">
          <div className="bg-white rounded-2xl border border-zinc-200/90 p-5 shadow-xs space-y-3">
            <h3 className="text-xs font-black uppercase tracking-wider text-zinc-400">
              Setores • {preset.badgeLabel}
            </h3>

            <div className="space-y-2">
              
              {/* VERTICAL PET */}
              {activeVertical === 'pet_vet' && (
                <>
                  <button
                    type="button"
                    onClick={() => onNavigate('calendar')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-blue-300 hover:bg-blue-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-blue-50 text-blue-700 rounded-lg shrink-0">
                        <Calendar className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Agenda da Clínica & Pet Shop
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Consultas veterinárias, banho e tosa
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-blue-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('prontuario_soap')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-emerald-300 hover:bg-emerald-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-emerald-50 text-emerald-700 rounded-lg shrink-0">
                        <Stethoscope className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Ficha Clínica do Pet
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Histórico de consultas e prescrições
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-emerald-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('retorno_vacinas')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-purple-300 hover:bg-purple-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-purple-50 text-purple-700 rounded-lg shrink-0">
                        <Syringe className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Lembretes de Vacinas por WhatsApp
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Avisar tutores sobre reforços
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-purple-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('estoque_hub')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-indigo-300 hover:bg-indigo-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-indigo-50 text-indigo-700 rounded-lg shrink-0">
                        <Boxes className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Medicamentos, Vacinas & Produtos
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Estoque com validade e quantidade
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-indigo-600 transition-colors shrink-0" />
                  </button>
                </>
              )}

              {/* VERTICAL MÉDICO */}
              {activeVertical === 'medical' && (
                <>
                  <button
                    type="button"
                    onClick={() => onNavigate('calendar')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-blue-300 hover:bg-blue-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-blue-50 text-blue-700 rounded-lg shrink-0">
                        <Calendar className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Agenda de Consultas
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Horários, retornos e procedimentos
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-blue-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('prontuario_soap')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-emerald-300 hover:bg-emerald-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-emerald-50 text-emerald-700 rounded-lg shrink-0">
                        <Stethoscope className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Prontuário e Histórico do Paciente
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Evolução clínica e receitas
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-emerald-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('cofre_exames')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-indigo-300 hover:bg-indigo-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-indigo-50 text-indigo-700 rounded-lg shrink-0">
                        <FileArchive className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Exames e Laudos Anexados
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Guarda de exames e fotos
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-indigo-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('portal_contador')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-amber-300 hover:bg-amber-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-amber-50 text-amber-700 rounded-lg shrink-0">
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Envio de Recibos para o Contador
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Relatório mensal para a contabilidade
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-amber-600 transition-colors shrink-0" />
                  </button>
                </>
              )}

              {/* VERTICAL TÉCNICO & SEGUROS */}
              {activeVertical === 'technical_service' && (
                <>
                  <button
                    type="button"
                    onClick={() => onNavigate('work-orders')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-amber-300 hover:bg-amber-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-amber-50 text-amber-700 rounded-lg shrink-0">
                        <ClipboardList className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Ordens de Serviço & Consertos
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Acompanhamento de serviços e seguradoras
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-amber-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('calendar')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-blue-300 hover:bg-blue-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-blue-50 text-blue-700 rounded-lg shrink-0">
                        <Calendar className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Agendamento de Serviços & Vistorias
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Entrada de veículos e vistorias
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-blue-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('estoque_hub')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-indigo-300 hover:bg-indigo-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-indigo-50 text-indigo-700 rounded-lg shrink-0">
                        <Boxes className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Peças de Reposição & Materiais
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Estoque de peças e insumos
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-indigo-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('signatures')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-purple-300 hover:bg-purple-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-purple-50 text-purple-700 rounded-lg shrink-0">
                        <PenTool className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Termo de Garantia Assinado
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Assinatura feita no celular do cliente
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-purple-600 transition-colors shrink-0" />
                  </button>
                </>
              )}

              {/* VERTICAL GERAL */}
              {activeVertical === 'general_corporate' && (
                <>
                  <button
                    type="button"
                    onClick={() => onNavigate('history')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-blue-300 hover:bg-blue-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-blue-50 text-blue-700 rounded-lg shrink-0">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Orçamentos & Propostas
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Propostas comerciais para clientes
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-blue-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('work-orders')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-amber-300 hover:bg-amber-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-amber-50 text-amber-700 rounded-lg shrink-0">
                        <ClipboardList className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Ordens de Serviço
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Execução e entrega de projetos
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-amber-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('estoque_hub')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-indigo-300 hover:bg-indigo-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-indigo-50 text-indigo-700 rounded-lg shrink-0">
                        <Boxes className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Estoque & Lotes
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Controle de materiais em estoque
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-indigo-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('portal_contador')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-emerald-300 hover:bg-emerald-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-emerald-50 text-emerald-700 rounded-lg shrink-0">
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Envio de Relatórios para o Contador
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Fechamento contábil mensal
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-emerald-600 transition-colors shrink-0" />
                  </button>
                </>
              )}

              {/* VERTICAL SORVETERIA, AÇAÍ & VAREJO/GRANEL */}
              {(activeVertical === 'food_icecream' || activeVertical === 'retail_bulk') && (
                <>
                  <button
                    type="button"
                    onClick={() => onNavigate('speed_pos')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-blue-300 hover:bg-blue-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-blue-50 text-blue-700 rounded-lg shrink-0">
                        <Scale className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          {activeVertical === 'retail_bulk' ? 'Speed-Checkout & Granel' : 'Speed-Checkout & Pesagem'}
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          {activeVertical === 'retail_bulk' ? 'Frente de caixa com balança, etiquetas e comandas' : 'Frente de caixa rápida com balança e comandas'}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-blue-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('receipts')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-emerald-300 hover:bg-emerald-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-emerald-50 text-emerald-700 rounded-lg shrink-0">
                        <ReceiptIcon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Cupons & Vendas Finalizadas
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Extrato de vendas e recibos emitidos no caixa
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-emerald-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('estoque_hub')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-indigo-300 hover:bg-indigo-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-indigo-50 text-indigo-700 rounded-lg shrink-0">
                        <Boxes className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          {activeVertical === 'retail_bulk' ? 'Estoque, Lotes & Sacarias' : 'Estoque de Insumos & Frutas'}
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          {activeVertical === 'retail_bulk' ? 'Controle de validades, FIFO e entrada por NF-e' : 'Controle de validades, embalagens e caldas'}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-indigo-600 transition-colors shrink-0" />
                  </button>

                  <button
                    type="button"
                    onClick={() => onNavigate('portal_contador')}
                    className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-amber-300 hover:bg-amber-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-amber-50 text-amber-700 rounded-lg shrink-0">
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <strong className="text-xs font-black text-zinc-900 block truncate">
                          Fechamento Mensal para Contador
                        </strong>
                        <span className="text-[11px] text-zinc-400 block truncate">
                          Exportação e relatórios contábeis
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-amber-600 transition-colors shrink-0" />
                  </button>
                </>
              )}

              {/* Botão de Equipe (Comum a todas as verticais) */}
              <button
                type="button"
                onClick={onOpenTeamModal}
                className="w-full p-3 rounded-xl border border-zinc-200/80 hover:border-purple-300 hover:bg-purple-50/40 transition-all flex items-center justify-between text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-purple-50 text-purple-700 rounded-lg shrink-0">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <strong className="text-xs font-black text-zinc-900 block truncate">
                      Funcionários & Colaboradores
                    </strong>
                    <span className="text-[11px] text-zinc-400 block truncate">
                      Acesso e permissões por funcionário
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-purple-600 transition-colors shrink-0" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
